import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, Subject, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private _httpClient = inject(HttpClient);
  private _utilsService = inject(UtilsService);

  loadGoogleMapsScript(lang: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const scriptId = 'google-maps-script';
      if (document.getElementById(scriptId)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.mapsKey}&libraries=places,geometry,marker&language=${lang}&region=ES${!this._utilsService.isIOS() ? '&loading=async' : ''}`;
      script.defer = true;
      script.onload = (): void => resolve();
      script.onerror = (): void => reject();
      document.body.appendChild(script);
    });
  }

  getRoute(origin: any, destination: any): Observable<any> {
    const data: any = {
      origin: {
        location: {
          latLng: {
            latitude: origin.lat,
            longitude: origin.lng,
          },
        },
      },
      /*destination: {
        address: destination,
      },*/
      destination: {
        location: {
          latLng: {
            latitude: destination.lat,
            longitude: destination.lng,
          },
        },
      },
      travelMode: 'DRIVE',
      routingPreference: 'TRAFFIC_AWARE',
      computeAlternativeRoutes: false,
      routeModifiers: {
        avoidTolls: false,
        avoidHighways: false,
        avoidFerries: false,
      },
      languageCode: 'es-ES',
      units: 'METRIC',
    };

    let headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
    })
      .append('X-Goog-Api-Key', environment.mapsKey)
      .append(
        'X-Goog-FieldMask',
        'routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline'
      );
    if (this._utilsService.isApp()) {
      headers = headers.append('Referer', environment.pServer);
    }

    return this._httpClient
      .post('https://routes.googleapis.com/directions/v2:computeRoutes', data, {
        headers: headers,
      })
      .pipe(
        map((response: any) => {
          const encodedPolyline = response?.routes?.[0]?.polyline?.encodedPolyline;
          if (encodedPolyline && window.google?.maps?.geometry?.encoding?.decodePath) {
            return window.google.maps.geometry.encoding.decodePath(encodedPolyline);
          }
          return [];
        })
      );
  }

  newMap(
    map: google.maps.Map,
    element: string,
    options: google.maps.MapOptions
  ): Observable<google.maps.Map> {
    const subject = new Subject<any>();

    google.maps.importLibrary('maps').then(googleLib => {
      const { Map } = googleLib as google.maps.MapsLibrary;
      map = new Map(document.getElementById(element) as HTMLElement, {
        ...options,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        mapId: element,
        clickableIcons: false,
      });
      subject.next(map);
    });

    return subject.asObservable();
  }

  getNdrawRoute(
    origin: any,
    destination: any,
    destinationText: string,
    map: google.maps.Map
  ): void {
    this.getRoute(origin, destination)
      .pipe(take(1))
      .subscribe(path => {
        this.setPolyline(path, origin, destination, map);
      });
  }

  setMarker(
    infoPosition: any,
    map: google.maps.Map,
    label?: string,
    selected?: boolean
  ): google.maps.marker.AdvancedMarkerElement {
    let pinTextGlyph = new google.maps.marker.PinElement({
      glyphColor: 'white',
      background: '#0033ff',
      borderColor: '#fff',
    });
    if (selected) {
      pinTextGlyph = new google.maps.marker.PinElement({
        glyphColor: 'white',
        background: '#0033ff',
        borderColor: '#fff',
        scale: 1.5,
      });
    }
    pinTextGlyph.glyph = label;
    return new google.maps.marker.AdvancedMarkerElement({
      map: map,
      position: infoPosition,
      content: pinTextGlyph.element,
    });
  }

  setPolyline(
    path: any,
    originPosition: any,
    destinationPosition: any,
    map: google.maps.Map
  ): void {
    this.setMarker(destinationPosition, map);
    this.setMarker(originPosition, map, 'A');

    if (path.length > 1) {
      const routePolyline = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#4285F4',
        strokeOpacity: 1.0,
        strokeWeight: 4,
      });
      routePolyline.setMap(map);
      this.fitBounds(
        path.map((item: any) => {
          return { lat: item.lat(), lng: item.lng() };
        }),
        map
      );
    } else {
      this.fitBounds([originPosition, destinationPosition], map);
    }
  }

  fitBounds(path: any, map: google.maps.Map): void {
    const bounds = new google.maps.LatLngBounds();

    path.forEach(function (item: any) {
      bounds.extend(new google.maps.LatLng(item.lat, item.lng));
    });
    map.setCenter(bounds.getCenter());
    map.fitBounds(bounds);
  }
}
